/// <reference types="google.maps" />

import { importLibrary } from '@googlemaps/js-api-loader';
import { AUTOCOMPLETE_DEFAULT_MIN_CHARS } from '@tundr/ds-components';
import { useDebounceFn } from '@vueuse/core';
import { computed, onScopeDispose, ref, shallowRef } from 'vue';
import {
  ADDRESS_PLACE_FIELDS,
  ADDRESS_PRIMARY_TYPES,
  useGeoConfig,
} from './geo.config.ts';
import { parsePlaceAddress } from './parse-place-address.ts';
import type { ParsedAddress, PlaceSuggestion } from './places.types.ts';

const DEFAULT_DEBOUNCE_MS = 250;

export type UsePlacesAutocompleteOptions = {
  minChars?: number;
  debounceMs?: number;
  onError?: (reason: unknown) => void;
};

export const usePlacesAutocomplete = ({
  minChars = AUTOCOMPLETE_DEFAULT_MIN_CHARS,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  onError,
}: UsePlacesAutocompleteOptions = {}) => {
  const config = useGeoConfig();

  const available = computed(() => Boolean(config?.googleMapsApiKey));
  const suggestions = shallowRef<PlaceSuggestion[]>([]);
  const loading = ref(false);
  const error = shallowRef<unknown>(null);

  let placesLibrary: Promise<google.maps.PlacesLibrary> | null = null;
  let sessionToken: google.maps.places.AutocompleteSessionToken | null = null;
  let requestId = 0;
  const predictions = new Map<string, google.maps.places.PlacePrediction>();

  const loadPlaces = () => {
    if (!available.value) {
      return Promise.reject(new Error('Google Maps API key is not configured'));
    }
    placesLibrary ??= importLibrary('places').catch((reason: unknown) => {
      placesLibrary = null;
      throw reason;
    });
    return placesLibrary;
  };

  const fail = (reason: unknown) => {
    error.value = reason;
    onError?.(reason);
  };

  const preload = async () => {
    try {
      await loadPlaces();
    } catch (reason) {
      fail(reason);
    }
  };

  const fetchSuggestions = async (input: string, id: number) => {
    try {
      const { AutocompleteSessionToken, AutocompleteSuggestion } =
        await loadPlaces();
      sessionToken ??= new AutocompleteSessionToken();
      const response =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          sessionToken,
          includedRegionCodes: [config?.region ?? 'it'],
          includedPrimaryTypes: ADDRESS_PRIMARY_TYPES,
          language: config?.language,
          region: config?.region,
        });
      if (id !== requestId) return;

      predictions.clear();
      suggestions.value = response.suggestions.flatMap(
        ({ placePrediction }) => {
          if (!placePrediction) return [];
          predictions.set(placePrediction.placeId, placePrediction);
          return [
            {
              placeId: placePrediction.placeId,
              mainText:
                placePrediction.mainText?.text ?? placePrediction.text.text,
              secondaryText: placePrediction.secondaryText?.text ?? '',
            },
          ];
        },
      );
      error.value = null;
    } catch (reason) {
      if (id !== requestId) return;
      suggestions.value = [];
      fail(reason);
    } finally {
      if (id === requestId) loading.value = false;
    }
  };

  const debouncedFetch = useDebounceFn(fetchSuggestions, debounceMs);

  const reset = () => {
    requestId += 1;
    suggestions.value = [];
    loading.value = false;
  };

  const search = (input: string) => {
    const query = input.trim();
    if (query.length < minChars) {
      reset();
      return;
    }
    requestId += 1;
    loading.value = true;
    debouncedFetch(query, requestId);
  };

  const resolve = async (placeId: string): Promise<ParsedAddress> => {
    const prediction = predictions.get(placeId);
    if (!prediction) {
      throw new Error(`Unknown place prediction: ${placeId}`);
    }
    reset();
    try {
      const { place } = await prediction
        .toPlace()
        .fetchFields({ fields: ADDRESS_PLACE_FIELDS });
      return parsePlaceAddress(place);
    } catch (reason) {
      fail(reason);
      throw reason;
    } finally {
      sessionToken = null;
      predictions.clear();
    }
  };

  onScopeDispose(() => {
    requestId += 1;
  });

  return {
    available,
    suggestions,
    loading,
    error,
    preload,
    search,
    resolve,
    reset,
  };
};
